import { ApplicationConfig, ErrorHandler, LOCALE_ID, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeEsAr from '@angular/common/locales/es-AR';
import { provideRouter, withViewTransitions } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideEchartsCore } from 'ngx-echarts';

import { routes } from './app.routes';
import { jwtInterceptor } from './core/interceptors/jwt.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { timeoutInterceptor } from './core/interceptors/timeout.interceptor';
import { GlobalErrorHandler } from './core/observability/global-error-handler';

// Locale es-AR (auditoría M4a): DatePipe/DecimalPipe formatean dd/mm/aaaa y miles con punto
// sin pasar locale explícito en cada uso. Los formatters con Intl ya usan 'es-AR' a mano.
registerLocaleData(localeEsAr);

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'es-AR' },
    provideBrowserGlobalErrorListeners(),
    // Los errores no manejados (de Angular y de window, vía los listeners globales)
    // pasan por el ErrorReporter (ERROR_REPORTER, consola por defecto) y luego siguen
    // el flujo normal. Para conectar una plataforma real ver docs/decisions/LIBRARY-DECISIONS.md.
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Cross-fade mínimo entre rutas (solo el área de contenido — ver ::view-transition-* en
    // styles.scss). Sin soporte del navegador o con prefers-reduced-motion, navegación normal.
    // onViewTransitionCreated (auditoría m9): al navegar rápido, el navegador aborta la
    // transición anterior y sus promesas ready/finished se rechazan con InvalidStateError
    // ("Transition was aborted because of invalid state"). Es un aborto esperado y benigno,
    // pero sin handler llega como unhandledrejection a la consola (y al ErrorReporter global).
    // Se absorbe acá: la transición abortada no requiere ninguna acción.
    provideRouter(
      routes,
      withViewTransitions({
        onViewTransitionCreated: ({ transition }) => {
          transition.ready.catch(() => undefined);
          transition.finished.catch(() => undefined);
        },
      }),
    ),
    // Orden importa: la respuesta viaja en sentido inverso al array, así que timeoutInterceptor
    // (último, más cerca del backend) convierte un TimeoutError en HttpErrorResponse ANTES de que
    // errorInterceptor lo vea y lo normalice a ApiError con el mensaje de fallo de red ya limpio.
    provideHttpClient(withInterceptors([jwtInterceptor, errorInterceptor, timeoutInterceptor])),
    provideAnimationsAsync(),
    provideEchartsCore({ echarts: () => import('./shared/echarts-setup').then((m) => m.echarts) }),
  ]
};
